import { InlineBlock } from '@components/Layout/Blocks';
import { Col } from '@components/Layout/Col';
import { Row } from '@components/Layout/Row';
import { I18n } from '@components/Trans';
import { PricePreview } from '@containers/Apparel/Orders/Edit/components/EditStepper/PricePreview';
import { AuthDialog } from '@containers/Auth/AuthDialog';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useUser } from '@hooks/useUser';
import Paper from '@material-ui/core/Paper';
import RootRef from '@material-ui/core/RootRef';
import { DialogApi } from '@packages/dialog/createDialog';
import { call } from '@packages/functions';
import { useSticky } from '@packages/hooks/useSticky';
import { Form, Formik } from 'formik';
import { isNil } from 'lodash';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ItemsEditClientSide from '../../Items/components/ItemsEditClientSide';
import { InstantQuoteContext } from './InstantQuoteStepper';
import IQHeadline from './IQHeadline';

export default function Review({ onApi }: any) {
    const submitRef = useRef<any>(null);
    const [quoteSubmitable, setQuoteSubmitable] = useState<boolean>(false);
    const [quoteName, setQuoteName] = useState(null);
    const { instantQuote, dispatch } = useContext<any>(InstantQuoteContext);
    const { order } = instantQuote as any;
    const user$ = useUser();
    const signinDialogRef = useRef<DialogApi | null>(null);

    const isMobile = useMediaQuery('max-960');
    const handleSigninDialog = useCallback(() => signinDialogRef.current?.setOpen(true), [signinDialogRef.current]);

    const handleSubmit = ({ name }, actions) => {
        if (isNil(user$)) {
            handleSigninDialog();
        }
        setQuoteSubmitable(true);
        setQuoteName(name);
        actions.setSubmitting(false);
    };

    useEffect(() => {
        if (!isNil(user$) && quoteSubmitable) {
            dispatch({
                type: 'createOrder',
                payload: {
                    ...order,
                    quoteName,
                },
            });
        }
    }, [user$, quoteSubmitable]);

    const handleUpdateItems = (items) => {
        dispatch({
            type: 'setOrder',
            payload: {
                ...instantQuote.order,
                items,
            },
        });
    };

    const handleDeleteItems = (items, deletedItem) => {
        dispatch({
            type: 'setOrder',
            payload: {
                ...instantQuote.order,
                items,
            },
        });

        // Options are items with a SKU (not package), so filter first for all SelectableSKU items and just return ID
        dispatch({
            type: 'setOptions',
            payload: items.filter((item) => !item.package && item.sku && item.sku.id),
        });

        dispatch({
            type: 'recalculateIsDone',
            payload: instantQuote.prices.filter(
                (price) => !(price.type === 'sku' && price.type_id === deletedItem.sku.id),
            ),
        });
    };

    const sticky = useSticky({
        offsetTop: 24,
        minWidth: 960,
    });

    useEffect(() => {
        call(onApi, {
            submit: () => {
                if (submitRef.current) {
                    submitRef.current?.click();
                }
            },
        });

        return () => {
            call(onApi, null);
        };
    }, []);

    const InstantQuoteForm = ({ isSubmitting }) => (
        <Form style={{ width: '100%' }}>
            <AuthDialog apiRef={signinDialogRef} from={'IQ'} />
            <Row layout={isMobile ? 'column' : 'row'}>
                <InlineBlock
                    w={'100%'}
                    maxWidth={isMobile ? '100%' : 'calc(100% - 320px)'}
                    p={isMobile ? 0 : '0 28px 0 0'}
                >
                    {order && order.items && order.items.length > 0 ? (
                        <ItemsEditClientSide
                            items={order.items}
                            orderId={order.id}
                            onUpdate={handleUpdateItems}
                            onDelete={handleDeleteItems}
                            showRowHeadline={true}
                            showTierPricing={true}
                        />
                    ) : (
                        <Paper component={Row} elevation={0} square={!isMobile}>
                            <Col align={'center'} justify={'center'} xs padding={24}>
                                <I18n variant={'subtitle2'} color={'textSecondary'}>
                                    There is no Packages or Extra options selected.
                                </I18n>
                            </Col>
                        </Paper>
                    )}
                </InlineBlock>

                <InlineBlock w={isMobile ? '100%' : 320}>
                    <RootRef rootRef={sticky.element}>
                        <PricePreview
                            prices={instantQuote.orderPrice}
                            loading={isSubmitting || instantQuote.creatingOrder || instantQuote.orderPriceLoading}
                            submitText={'Submit your quote request'}
                            submitRef={submitRef}
                            reverse={isMobile}
                            disableSubmit={
                                isSubmitting || instantQuote.creatingOrder || !order.items || order.items.length === 0
                            }
                        />
                    </RootRef>
                </InlineBlock>
            </Row>
        </Form>
    );

    return (
        <>
            <IQHeadline
                headTitle={'Instant Free Quote'}
                title={'3. Review your instant quote.'}
                text={'Review and adjust quantities to your instant quote.'}
            />

            <Row>
                <Formik initialValues={order} onSubmit={handleSubmit}>
                    {InstantQuoteForm}
                </Formik>
            </Row>
        </>
    );
}
